import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import Movie from "./Movie";
import { useNavigate, useParams } from "react-router-dom";
import { doc, getDoc, updateDoc, serverTimestamp } from "firebase/firestore";
import { UserContext } from '../UserContext';
import { db, auth } from "../config/firebasedeconfig.js";
import { AnimeGirl } from "./AnimeGirl.js";
import { Helmet } from "react-helmet-async"; // Import de Helmet
import { useLanguage } from '../contexts/LanguageContext';
import ActivityGainToast from "./ActivityGainToast";
import { XP_WEIGHTS } from "./levelUtils";

const CategoriesMovies = () => {
  const { selectedCategory } = useParams();
  const [data, setData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const navigate = useNavigate();
  const { t, currentLanguage } = useLanguage();

  const { isLoggedIn } = useContext(UserContext);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showCategoryLimitMessage, setShowCategoryLimitMessage] = useState(false);

  const [showCategoryConfirmation, setShowCategoryConfirmation] = useState(false);
  const [selectedCategoryName, setSelectedCategoryName] = useState("");
  const [activityToast, setActivityToast] = useState(null);

  const URL_GENRES = `https://api.themoviedb.org/3/genre/movie/list?api_key=e16900047627f56531a2d3d8d4df9e92&language=${currentLanguage === 'en' ? 'en-US' : currentLanguage + '-' + currentLanguage.toUpperCase()}`;

  const nextPage = () => {
    setCurrentPage((prevPage) => prevPage + 1);
  };

  const prevPage = () => {
    setCurrentPage((prevPage) => (prevPage > 1 ? prevPage - 1 : prevPage));
  };

  const fetchMovieDetails = async () => {
    try {
      const response = await axios.get(URL_GENRES);
      const category = response.data.genres.find(
        (genre) => genre.id === Number(selectedCategory)
      );
      if (category) {
        setSelectedCategoryName(category.name);
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    const fetchCategoryMovies = async () => {
      try {
        const response = await axios.get(
          `https://api.themoviedb.org/3/discover/movie?api_key=e16900047627f56531a2d3d8d4df9e92&language=${currentLanguage === 'en' ? 'en-US' : currentLanguage + '-' + currentLanguage.toUpperCase()}&sort_by=popularity.desc&include_adult=false&include_video=false&page=${currentPage}&with_genres=${selectedCategory}`
        );
        setData(response.data.results);
      } catch (error) {
        console.log(error);
      }
    };

    fetchCategoryMovies();
  }, [selectedCategory, currentPage, currentLanguage]);

  useEffect(() => {
    fetchMovieDetails();
  }, [selectedCategory, currentLanguage]);

  const clampFavoriteCount = (items = []) => {
    if (!Array.isArray(items)) {
      return 0;
    }

    return Math.min(items.length, 3);
  };

  const addFavoriteCategory = async () => {
    try {
      if (!isLoggedIn || !selectedCategoryName) return;

      const userId = auth.currentUser.uid;
      const userRef = doc(db, "users", userId);
      const userSnap = await getDoc(userRef);
      const userData = userSnap.data();

      if (!userSnap.exists()) {
        console.error("No such user!");
        return;
      }

      const currentFavorites = userData.favoriteCategories || [];

      // Vérifier si la catégorie est déjà dans les favoris
      if (currentFavorites.some(category => String(category.id) === String(selectedCategory))) {
        setShowCategoryConfirmation(true);
        setTimeout(() => {
          setShowCategoryConfirmation(false);
        }, 3000);
        return;
      }

      const newFavoriteCategories = [
        ...currentFavorites,
        { id: selectedCategory, name: selectedCategoryName },
      ];

      const nextFavoriteGenresCount = clampFavoriteCount(newFavoriteCategories);

      await updateDoc(userRef, {
        favoriteCategories: newFavoriteCategories,
        "xp.sources.favoriteGenres": nextFavoriteGenresCount,
        "xp.updatedAt": serverTimestamp(),
      });

      setActivityToast({
        toastId: Date.now(),
        points: `+${XP_WEIGHTS.favoriteGenres}`,
        variant: "favorite",
      });

      setShowCategoryConfirmation(true);
      setTimeout(() => {
        setShowCategoryConfirmation(false);
      }, 3000);
      setShowCategoryConfirmation(true);
      setTimeout(() => {
        setShowCategoryConfirmation(false);
      }, 3000);
    } catch (error) {
      console.error("Error adding favorite category:", error);
    }
  };

  return (
    <div className="categoriesMovies">
      {/* Helmet pour les balises SEO dynamiques */}
      <Helmet>
        <title>{selectedCategoryName ? `${t('categories.films')} - ${selectedCategoryName} | MovieFrenzy` : `${t('categories.filmCategories')} | MovieFrenzy`}</title>
        <link rel="canonical" href={`https://moviefrenzy.ca/categories/${selectedCategory}`} />
        <meta
          name="description"
          content={
            selectedCategoryName
              ? t('categories.metaDescriptionCategory').replace('{{category}}', selectedCategoryName)
              : t('categories.metaDescriptionGeneral')
          }
        />
      </Helmet>

      {selectedCategory && (
        <div className="containerCatTitle">
          <h1 className="titleCategory">{selectedCategoryName}</h1>
          {selectedCategoryName === "Animation" && <AnimeGirl isLoggedIn={isLoggedIn} />}
          {isLoggedIn && (
            <div
              className={`favoriteCategoryButton ${selectedCategoryName === "Animation" ? "animation-active" : ""}`}
              onClick={addFavoriteCategory}
              style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/images/cinemaFavoriteButtonWhite.png)` }}
            ></div>
          )}
        </div>
      )}

      {showCategoryConfirmation && (
        <h2
          className="CategoriesAjoutTitre"
          style={{ color: "#00FFFF", marginLeft: "5%", zIndex: "1000" }}
        >
          {t('categories.addedToFavorites')}
        </h2>
      )}

      {showCategoryLimitMessage && (
        <h2
          className="CategoriesAjoutTitre"
          style={{ color: "#FF0000", marginLeft: "5%", zIndex: "1000" }}
        >
          {t('categories.favoritesLimitReached')}
        </h2>
      )}

      <div className="categoriesMovies-content">
        {selectedCategory ? (
          <ul className="categoriesMovies-list">
            {data.map((m) => (
              <Movie
                key={m.id}
                movie={m}
                onClickMovie={() => navigate(`/ficheFilm/${m.id}`)}
              />
            ))}
          </ul>
        ) : (
          <div className="placeholder-container">
            <h4>{t('categories.nothingToShow')}</h4>
          </div>
        )}
      </div>

      <div className="pagination">
        <button onClick={prevPage}>{t('categories.previousPage')}</button>
        <span>{t('categories.page')} {currentPage}</span>
        <button onClick={nextPage}>{t('categories.nextPage')}</button>
      </div>

      {activityToast && (
        <ActivityGainToast
          visible={Boolean(activityToast)}
          toastId={activityToast.toastId}
          points={activityToast.points}
          unitLabel={t("profilMember.activity")}
          variant={activityToast.variant}
          className="activity-gain-toast--categories-movies"
          onDone={() => setActivityToast(null)}
        />
      )}
    </div>
  );
};

export default CategoriesMovies;

